import React, { useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer, Backdrop } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import './BottomMenuDrawer.scss';
import { RootState, Dispatch } from '../../../configs/store';
import BetSlipModal from '../../Betslip/BetSlipModal/BetSlipModal';
import Filter from '../../Filter/Filter';
import Icon from '../../Icon/Icon';
import SearchResults from '../../SearchResults/SearchResults';
import Search from '../../Filter/Search/Search';
import useDeviceScroll from 'src/hooks/useDeviceScroll';
import BottomMenuContext from '../Context/BottomMenu.context';

interface BottomMenuDrawerProps {
  open?: boolean,
  variant?: 'permanent' | 'persistent' | 'temporary',
  content: 'betslip' | 'filter' | 'myBets',
  anchor?: 'left' | 'top' | 'right' | 'bottom'
}

export default function BottomMenuDrawer({ content, open = false, variant = 'temporary', anchor = 'bottom' }: BottomMenuDrawerProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const filterRef = useRef(null);

  const ref = filterRef.current ?? window;
  const scrollPositionHook = useDeviceScroll(ref);

  const { handleOpen } = useContext(BottomMenuContext);
  // REDUX
  const { isSearching, searchProduct, loading, search, isCardCodeEvent } = useSelector((state: RootState) => state.events);
  const { filterResults, scrollPosition } = useSelector((state: RootState) => state.filters);
  const dispatch = useDispatch<Dispatch>();

  let product = 'sports';
  if (location.pathname.includes('/racing')) {
    product = 'racing';
  } else if (location.pathname.includes('/lotto')) {
    product = 'lotto';
  }

  const closeResults = async () => {
    if (search !== '') {
      await dispatch.events.resetSearchResults();
    } else {
      await dispatch.filters.setFilterResults([]);
      await dispatch.events.clearSelectedTop(true);
    }
  };

  useEffect(() => {
    if (product !== searchProduct && searchProduct !== '') {
      closeResults();
      dispatch.events.clearFilterTournaments();
    }
  }, []);

  useEffect(() => {
    if (ref !== window && open) {
      dispatch.filters.updateScrollPosition(scrollPositionHook);
    }
  }, [scrollPositionHook]);

  useEffect(() => {
    if (content === 'filter' && open) {
      filterRef.current.scrollTo(0, scrollPosition);
    }
  }, [open]);

  const getSearchResults = (value: string) => {
    dispatch.events.clearFilterTournaments();
    dispatch.events.getSearchResults({ q: value, product });
  };

  const renderChild = () => {
    switch (content) {
      case 'betslip':
        return <BetSlipModal />;

      case 'myBets':
        return <BetSlipModal myBets />;

      case 'filter':
        return (
          <div className="bottom-navigation__sidebar" ref={ filterRef }>
            {
              ((isSearching && searchProduct === product) || (filterResults.length > 0 && location.pathname.includes('/sports-betting')))
                ? (
                  <>
                    <div className={
                      classNames({
                        'bottom-navigation__buttons': true,
                        'bottom-navigation__buttons--right': isCardCodeEvent
                      })
                    }
                    >
                      {
                        !isCardCodeEvent &&
                      <button
                        className="bottom-navigation__btn"
                        onClick={ closeResults }
                      >
                        <Icon icon="arrow" className="bottom-navigation__back" />
                        { t('filterSports.backToFilters') }
                      </button>
                      }
                      <button
                        className="bottom-navigation__btn"
                        onClick={ () => handleOpen('') }
                      >
                        <Icon icon="close" className="bottom-navigation__close" />
                      </button>
                    </div>
                    {
                      isSearching && !loading && (
                        <div className="bottom-navigation__search">
                          <Search
                            placeholder={ t('filterSports.searchPlaceholder')  }
                            searchHelper={ t('filterSports.searchHelper')  }
                            getSearchResults={ getSearchResults }
                            handleReset={ dispatch.events.resetSearchResults }
                            minLength={ 3 }
                          />
                        </div>
                      )
                    }
                    {
                      product === 'sports' &&
                      <SearchResults>
                        <SearchResults.Sports />
                      </SearchResults>
                    }
                    {
                      product === 'racing' &&
                      <SearchResults>
                        <SearchResults.Racing />
                      </SearchResults>
                    }
                  </>
                ) : (
                  <>
                    <div className="bottom-navigation__title">{ t('searchButton') }
                      <button
                        className="bottom-navigation__btn bottom-navigation__btn--close"
                        onClick={ () => handleOpen('') }
                      >
                        <Icon icon="close" className="bottom-navigation__close" />
                      </button>
                    </div>
                    <Filter />
                  </>
                )
            }
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Drawer
        className={
          classNames('bottom-navigation-drawer', {
            [`bottom-navigation-drawer--${ content }`]: !!content
          })
        }
        anchor={ anchor }
        variant={ variant }
        open={ open }
      >
        { renderChild() }
      </Drawer>
      <Backdrop open={ open } />
    </>
  );
}
